import React, { useMemo } from 'react';

import { isClaimBarFieldAvailableForOrganization } from '~/components/ClaimPage/ClaimPageBar/utils';
import type { ClaimBarFieldConfiguration } from '~/components/ClaimPage/types';
import DndOrderedList from '~/components/core/DndOrderedList/DndOrderedList';
import type { DndOrderedListReorder } from '~/components/core/DndOrderedList/DndOrderedList.types';
import useOrganization from '~/components/OrganizationContext';

import BarFieldConfigCard from './components/BarFieldConfigCard';
import { BAR_TEXTS as TEXTS } from './texts';
import type { ClaimBarFieldEntry } from './types';

export type BarFieldKey = ClaimBarFieldConfiguration['field_key'];

export const useDisplayedClaimBarFields = <T extends ClaimBarFieldEntry>(fields: T[]): T[] => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error TS233  useOrganization() not properly typed yet
  const { isClaimSearchEnabled } = useOrganization();
  return useMemo(
    () => fields.filter((field) => isClaimBarFieldAvailableForOrganization(field.field_key, isClaimSearchEnabled)),
    [fields, isClaimSearchEnabled]
  );
};

type ClaimBarFieldListProps = {
  /** Already filtered by `useDisplayedClaimBarFields`, so a count above the list and the list agree. */
  displayedFields: ClaimBarFieldEntry[];
  onToggleEnabled: (fieldKey: BarFieldKey, isEnabled: boolean) => void;
  onDisplayNameChange: (fieldKey: BarFieldKey, displayName: string) => void;
  onReorder: (reorder: DndOrderedListReorder) => void;
  disabled?: boolean;
  dimHiddenFields?: boolean;
  /** For a screen with one Save: names are handed on as typed, and no card has a Save of its own. */
  holdTypedNames?: boolean;
  fitTitlesToControls?: boolean;
};

const ClaimBarFieldList = ({
  displayedFields,
  onToggleEnabled,
  onDisplayNameChange,
  onReorder,
  disabled = false,
  dimHiddenFields = false,
  holdTypedNames = false,
  fitTitlesToControls = false,
}: ClaimBarFieldListProps) => {
  // The Flags entry is on for every organization and cannot be switched off: hiding it would strand
  // the flags a claim already carries, and this field is the only place to see or clear them. It
  // costs an organization with nothing configured nothing, because the claim bar decides at render
  // time whether the field actually appears.
  //
  // Unconditional, deliberately not keyed on the entry's own is_enabled. The server guarantees that
  // value: the update endpoint refuses to disable it, import and config-repo apply coerce it, and the
  // migration repaired the rows that predate all three. Keying the lock on a value the server already
  // guarantees would mean a regression there degraded into an unlocked toggle an administrator could
  // then switch off for real, instead of a visible failure.
  const isToggleLocked = (field: ClaimBarFieldEntry) => field.field_key === 'claim_flags';

  return (
    <DndOrderedList
      className="flex flex-col gap-12"
      disabled={disabled}
      items={displayedFields}
      getItemId={(field) => field.field_key}
      renderItem={(field, { dragHandleProps }) => (
        <BarFieldConfigCard
          field={field}
          dragHandleProps={dragHandleProps}
          onToggleEnabled={() => onToggleEnabled(field.field_key, !field.is_enabled)}
          onDisplayNameChange={(displayName) => onDisplayNameChange(field.field_key, displayName)}
          disabled={disabled}
          isToggleLocked={isToggleLocked(field)}
          toggleLockReason={TEXTS.claimFlagsToggleLockedTooltip}
          dimWhenHidden={dimHiddenFields}
          holdsTypedName={holdTypedNames}
          fitTitleToControls={fitTitlesToControls}
        />
      )}
      onReorder={onReorder}
    />
  );
};

export default ClaimBarFieldList;
